import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingBag, ChevronRight, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { formatPrice } from "@/lib/cart";
import api from "@/lib/api";

const STATUS_BADGE = {
  COMPLETED: "bg-[hsl(142,42%,34%,0.15)] text-[hsl(142,42%,26%)] border border-[hsl(142,42%,34%,0.25)]",
  APPROVED: "bg-[hsl(142,42%,34%,0.15)] text-[hsl(142,42%,26%)] border border-[hsl(142,42%,34%,0.25)]",
  PAYMENT_FAILED: "bg-[hsl(6,72%,46%,0.12)] text-[hsl(6,72%,36%)] border border-[hsl(6,72%,46%,0.22)]",
  FAILED: "bg-[hsl(6,72%,46%,0.12)] text-[hsl(6,72%,36%)] border border-[hsl(6,72%,46%,0.22)]",
  OPEN: "bg-[hsl(38,92%,45%,0.15)] text-[hsl(38,92%,30%)] border border-[hsl(38,92%,45%,0.25)]",
  PAYMENT_PENDING: "bg-[hsl(38,92%,45%,0.15)] text-[hsl(38,92%,30%)] border border-[hsl(38,92%,45%,0.25)]",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shop/me/orders")
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell variant="member" title="My Orders">
      <div className="max-w-3xl" data-testid="orders-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-[hsl(var(--fg))]" data-testid="orders-page-title">
            My Orders
          </h1>
          <Link to="/shop">
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="orders-shop-link">
              <ShoppingBag className="h-3.5 w-3.5" /> Shop
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border border-[hsl(var(--border))]">
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16" data-testid="orders-empty">
            <Package className="h-12 w-12 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
            <p className="text-lg font-medium text-[hsl(var(--fg))]">No orders yet</p>
            <p className="text-sm text-[hsl(var(--muted-fg))] mt-1">Your order history will appear here.</p>
            <Link to="/shop">
              <Button className="mt-4 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]" data-testid="orders-browse-shop">
                Browse Shop
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <Link key={order.id} to={`/shop/order/${order.id}`} data-testid={`order-card-${order.id}`}>
                <Card className="border border-[hsl(var(--border))] hover:shadow-[var(--shadow-md)] transition-shadow duration-200 cursor-pointer">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0">
                      <ShoppingBag className="h-5 w-5 text-[hsl(var(--muted-fg))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-[hsl(var(--fg))] truncate">
                          {(order.line_items || []).map((li) => li.name).join(", ") || "Order"}
                        </span>
                        <Badge className={STATUS_BADGE[order.status] || STATUS_BADGE.OPEN}>
                          {order.status || "Unknown"}
                        </Badge>
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-fg))] mt-0.5">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleDateString("en-AU", {
                              day: "numeric", month: "short", year: "numeric",
                            })
                          : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-semibold">
                        {order.total_money
                          ? formatPrice(order.total_money.amount, order.total_money.currency)
                          : "—"}
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[hsl(var(--muted-fg))]" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
